import { useCallback } from 'react';
import { api, MarketplaceEntity, MarketplaceEntitySearchResult, EntityType } from '../../lib/api';
import { buildSearchKey, usePagedSearch } from './usePagedSearch';

interface UseMarketplaceEntitySearchOptions {
  query?: string;
  categoryId?: string;
  type?: EntityType;
  sortBy?: 'relevance' | 'fitness' | 'popular' | 'newest';
  limit?: number;
}

interface UseMarketplaceEntitySearchReturn {
  results: MarketplaceEntitySearchResult | null;
  loading: boolean;
  error: string | null;
  entities: MarketplaceEntity[];
  total: number;
  hasMore: boolean;
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  refresh: () => void;
}

export function useMarketplaceEntitySearch(
  options: UseMarketplaceEntitySearchOptions = {},
): UseMarketplaceEntitySearchReturn {
  const { query, categoryId, type, sortBy, limit = 20 } = options;

  const searchKey = buildSearchKey([query, categoryId, type, sortBy]);

  const request = useCallback(
    (requestLimit: number, offset: number, signal: AbortSignal) =>
      api.searchMarketplaceEntities({
        q: query,
        categoryId,
        type,
        sortBy,
        limit: requestLimit,
        offset,
        signal,
      }),
    // Intentionally keyed on searchKey alone: it already encodes every
    // parameter below, and depending on them directly would reintroduce the
    // identity trap this replaced.
    [searchKey],
  );

  const { results, loading, error, page, setPage, refresh } =
    usePagedSearch<MarketplaceEntitySearchResult>({
      searchKey,
      limit,
      request,
      telemetryParams: { q: query, categoryId, type, sortBy },
      telemetry: {
        traceName: 'marketplaceEntitySearch',
        startMessage: 'Starting entity search',
        resultMessage: (result) => `Entity search returned ${result.entities.length} entities`,
        resultDetails: (result) => ({
          entityCount: result.entities.length,
          total: result.total,
          hasMore: result.hasMore,
          entityIds: result.entities.slice(0, 5).map((e) => e.entityId),
        }),
        failureMessage: (message) => `Entity search failed: ${message}`,
      },
      fallbackErrorMessage: 'Failed to search entities',
    });

  const entities = results?.entities ?? [];
  const total = results?.total ?? 0;
  const hasMore = results?.hasMore ?? false;
  const totalPages = Math.ceil(total / limit);

  return {
    results,
    loading,
    error,
    entities,
    total,
    hasMore,
    page,
    setPage,
    totalPages,
    refresh,
  };
}
